import type { FC } from 'react';
import { useEffect, useMemo } from 'react';

import { Outlet, useNavigate, useSearchParams } from 'react-router';
import { FeatureFlag } from '@/services/feature-flags';
import { useFlag } from '@/services/feature-flags/react';

import AppTopBar from '@/app/components/AppTopBar';
import { ExpiredBanner } from '@/app/components/ExpiredBanner';
import { ImpersonatorBanner } from '@/app/components/ImpersonatorBanner';
import { NavigationMenu } from '@/app/components/NavigationMenu';
import { VersionUpdateBanner } from '@/app/components/VersionUpdateBanner';
import { Permission } from '@/entities/permissions';
import UpgradeToPremiumButton from '@/features/_shared/UpgradeToPremium/UpgradeToPremiumButton';
import { usePermission, useUserDetails } from '@/features/_shared/user';
import { AskGXDrawer } from '@/features/ai-chat';
import { useSavedFiltersSync } from '@/features/catalog/api/savedFilters';
import { useDashboardSync } from '@/features/dashboard/hooks';
import ExportChartButton from '@/features/data-hub/components/ExportChartButton';
import { ExplainerVideoModal, useExplainerVideoModal } from '@/features/explainer-video';
import FactsheetDialog from '@/features/factsheet/components/FactsheetDialog';
import { ExportChartButtonContext } from '@/features/factsheet/state/ExportChartButtonContext';
import type { ChartButtonRenderer } from '@/features/factsheet/state/ExportChartButtonContext';
import { FactsheetOpenStateProvider } from '@/features/factsheet/state/FactsheetOpenStateProvider';
import { FactsheetOverlayContainerProvider } from '@/features/factsheet/state/FactsheetOverlayContainerProvider';
import type { FactsheetSelectionState } from '@/features/factsheet/state/FactsheetSelectionContext';
import { useExpiredBannerContent } from '@/features/profile/hooks/useExpiredBannerContent';
import { useSelectionFunctions } from '@/features/symbol-selector';
import { PageTitleProvider } from '@/shared/pageTitle';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { closeDrawer, DrawerType, openDrawer } from '@/store/slices/drawer.slice';

const renderExportChartButton: ChartButtonRenderer = (graphType, sx, iconSize) => (
  <ExportChartButton
    graphType={graphType}
    iconSize={iconSize}
    sx={sx}
  />
);

const shouldShowUpgradeButton = (
  isUserExpired: boolean | undefined,
  hasPremiumWidgetPermission: boolean,
  hasExpiredBannerContent: boolean
) => isUserExpired !== undefined && !hasPremiumWidgetPermission && !hasExpiredBannerContent;

const shouldShowAskGXDrawer = (isAiChatEnabled: boolean, isUserExpired: boolean | undefined) =>
  isAiChatEnabled && !isUserExpired;

const useAppLayoutRedirects = (impersonatorEmail: string | null | undefined) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const tripTokenState = useAppSelector(state => state.tripToken);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('prompt')) {
      dispatch(openDrawer(DrawerType.ASK_GX));
    }
  }, [searchParams, dispatch]);

  useEffect(() => {
    // When impersonation prevents T&C acceptance, stay on the requested route.
    if (impersonatorEmail) {
      return;
    }
    if (tripTokenState.status === 'fulfilled' && !tripTokenState.terms) {
      void navigate('/welcome');
    }
  }, [tripTokenState.status, tripTokenState.terms, impersonatorEmail, navigate]);
};

/**
 * Main application layout with navigation, banners, and content area.
 *
 * Features:
 * - Persistent navigation menu (sidebar)
 * - Version update banner
 * - Expired subscription banner
 * - Upgrade to premium button (for basic users)
 * - AI chat integration (feature-flagged)
 * - Factsheet dialog handling
 * - Terms acceptance check and redirect
 * - Responsive container with max-width constraints
 *
 * This layout wraps all authenticated application pages.
 */
export const AppLayout: FC = () => {
  const dispatch = useAppDispatch();
  useDashboardSync();
  useSavedFiltersSync();
  const openDrawerType = useAppSelector(state => state.drawer.openDrawer);
  const { isUserExpired, impersonatorEmail } = useUserDetails();
  const { hasPermission } = usePermission();
  const expiredBannerContent = useExpiredBannerContent();
  const showUpgradeButton = useMemo(
    () =>
      shouldShowUpgradeButton(
        isUserExpired,
        hasPermission(Permission.DashboardPremiumWidgets),
        Boolean(expiredBannerContent)
      ),
    [expiredBannerContent, hasPermission, isUserExpired]
  );
  const isAiChatEnabled = useFlag(FeatureFlag.AiChatEnabled);
  const showAskGXButton = shouldShowAskGXDrawer(isAiChatEnabled, isUserExpired);
  const { isOpen: isExplainerVideoOpen, onDismiss: onExplainerVideoDismiss } =
    useExplainerVideoModal();

  useAppLayoutRedirects(impersonatorEmail);

  const {
    isSymbolSelected,
    toggleSymbolInSelection,
    addSymbolsToSelection,
    removeSymbolsFromSelection,
    areAllSymbolsSelected,
    isSpotLocked
  } = useSelectionFunctions();
  const factsheetSelectionState: FactsheetSelectionState = {
    isSymbolSelected,
    toggleSymbolInSelection,
    addSymbolsToSelection,
    removeSymbolsFromSelection,
    areAllSymbolsSelected,
    isSpotLocked
  };

  return (
    <div
      data-slot='app-layout'
      className='mx-auto flex h-screen w-full max-w-[2000px] overflow-hidden xl:border-l xl:border-gx-tile-light'
    >
      <FactsheetOverlayContainerProvider>
        <FactsheetOpenStateProvider>
          <ExportChartButtonContext value={renderExportChartButton}>
            <FactsheetDialog selectionState={factsheetSelectionState} />
          </ExportChartButtonContext>
          <ExplainerVideoModal
            open={isExplainerVideoOpen}
            onClose={onExplainerVideoDismiss}
          />
          <NavigationMenu>
            <PageTitleProvider>
              <div className='relative box-border flex h-screen w-full min-w-0 flex-col'>
                <VersionUpdateBanner />
                <ExpiredBanner />
                <AppTopBar />
                <div className='flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto p-6'>
                  <ImpersonatorBanner />
                  {showUpgradeButton && (
                    <div className='flex justify-end gap-6'>
                      <UpgradeToPremiumButton
                        size='large'
                        sx={{ flexShrink: 0, whiteSpace: 'nowrap', fontSize: 16 }}
                        type='upgrade'
                      />
                    </div>
                  )}
                  {/* The Ask-GX trigger now lives in the sidebar (see useAppMenuSchema);
                      the drawer stays mounted here, controlled by drawer state. */}
                  {showAskGXButton && (
                    <AskGXDrawer
                      open={openDrawerType === DrawerType.ASK_GX}
                      onClose={() => {
                        dispatch(closeDrawer());
                      }}
                    />
                  )}
                  <Outlet />
                </div>
              </div>
            </PageTitleProvider>
          </NavigationMenu>
        </FactsheetOpenStateProvider>
      </FactsheetOverlayContainerProvider>
    </div>
  );
};
